"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The auth callback paths. Named once, here.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **This file exists because three places named these paths and one disagreed.**
 * `hard-won.md` WP34-2: the API built links to `/auth/verify-email`, the web app served
 * `/auth/verify`, and `supabase/config.toml` allow-listed the app's spelling. **Every signup
 * confirmation link was broken** — it landed on an unmatched route — and nothing failed anywhere
 * a test could see it, because each of the three was internally consistent.
 *
 * Four consumers have to agree, and they are in different languages:
 *
 * - **GoTrue**, via `additional_redirect_urls` in `supabase/config.toml`. A path not on that
 *   list is refused at redirect time, which surfaces as a generic auth error;
 * - **the API**, when it composes a link into an email;
 * - **the web app**, when it registers the route that receives one;
 * - **the reachability check**, which asserts each of those routes renders its own screen.
 *
 * `packages/shared/src/__tests__/auth-paths.test.ts` holds `config.toml` in step with this
 * module. That is the only mechanism that can catch the WP34-2 shape, because the disagreement
 * is between a TOML file and a TypeScript constant and no compiler reads both.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUTH_PATH_LIST = exports.AUTH_PATHS = void 0;
exports.authRedirectUrl = authRedirectUrl;
/**
 * ⚠️ **Leading slash, no trailing slash, no origin.** The origin is `APP_ORIGIN`, which the
 * Supabase CLI substitutes into `config.toml` separately — baking it in here would give the
 * paths an environment, and then a staging deployment needs a second copy of the list.
 */
exports.AUTH_PATHS = {
    /** Where GoTrue returns after an OAuth provider round trip. */
    oauthCallback: '/auth/callback',
    /** Where a signup confirmation link lands. */
    verifyEmail: '/auth/verify-email',
    /** Where a password-reset link lands. */
    resetPassword: '/auth/reset-password',
};
/** Every path, for a caller that needs the list rather than one member. */
exports.AUTH_PATH_LIST = Object.values(exports.AUTH_PATHS);
/**
 * The absolute URL GoTrue should redirect to, for one path.
 *
 * ⚠️ **`new URL(path, origin)` rather than string concatenation.** `${origin}${path}` produces
 * `http://localhost:5173//auth/callback` the moment somebody sets `APP_ORIGIN` with a trailing
 * slash — a URL that GoTrue's allow-list does not match, and that looks correct in a log.
 * `env.ts`'s `httpOrigin` already refuses a trailing path, but this is the site that would break
 * if that ever loosened.
 */
function authRedirectUrl(origin, path) {
    return new URL(path, origin).toString();
}
//# sourceMappingURL=auth-paths.js.map